import * as api from "api/Knowledge";
import { FeedType, PublicationType, PublicationVisility } from "models/ApiEnums";
import { CourseItemCompleteResult, CourseItemOperationResult, FeedActionsFilter, FeedAnswersFilter, FeedAnswersOrderBy, FeedBaseOrderBy, FeedContentDto, FeedContentReplace, FeedContentUpdate, FeedCourseEnrollFilter, FeedCourseFilter, FeedCourseListFilter, FeedCourseOrderBy, FeedFollowingFilter, FeedHighlightsFilter, FeedHistoryFilter, FeedItemDto, FeedListFilter, FeedListOrderBy, FeedPublicationFilter, FeedQuestionsFilter, FeedRequestDto, FeedTopicFilter, FeedTopicOrderBy, TimelineUserProfileFilter } from "models/ApiModels";
import { Action } from "redux";
import { ThunkAction } from "redux-thunk";
import Session from "services/Session";
import { updateAppCallouts } from "store/app/actions";
import { FeedMessageKeys, FeedMessageTypes } from "store/feed/windowMessages";
import { ApplicationState, FeedInstances } from "store/rootReducer";
import { CourseActions } from "views/Publication/Components/CourseModels";
import { FeedActionKeys } from "./keys";
import { IFeedAddAction, IFeedDeleteAction, IFeedExecutingAction, IFeedFetchFailureAction, IFeedFetchRequestAction, IFeedFetchResult, IFeedFetchSuccessAction, IFeedReplaceAction, IFeedResetAction, IFeedTryDeletePublication, IFeedTryUpdatePublication, IFeedUpdateAction } from "./types";

////////////////////////////

let lastRequest = 0;

////////////////////////////

export function computeFeedResult(startIndex: number, endIndex: number, result: FeedRequestDto): IFeedFetchResult {
    const rows = (endIndex - startIndex);
    const hasMore = result.items.length > rows;

    if (hasMore) result.items = result.items.slice(0, rows);

    return { content: result.items, hasMore: hasMore, totalRecords: result.total };
}


//////////////////////////////////////////////////////


class FeedActions {
    constructor(private instanceName: string) { }

    protected fetchStarted(): IFeedFetchRequestAction {
        return { type: FeedActionKeys.FEED_GET_REQUEST, instanceName: this.instanceName };
    }

    protected fetchFailure(data: any): IFeedFetchFailureAction {
        return { type: FeedActionKeys.FEED_GET_FAILURE, instanceName: this.instanceName, payload: data };
    }

    protected fetchSuccess(data: IFeedFetchResult): IFeedFetchSuccessAction {
        return { type: FeedActionKeys.FEED_GET_SUCCESS, instanceName: this.instanceName, payload: data };
    }

    protected deleteSuccess(uid: string, publicationId: string): IFeedDeleteAction {
        return { type: FeedActionKeys.FEED_DELETE_DATA, instanceName: this.instanceName, payload: { uid, publicationId } };
    }

    protected tryUpdateFeedData(data: DeepPartial<FeedItemDto>): IFeedTryUpdatePublication {
        return { type: FeedActionKeys.FEED_TRY_UPDATE_PUBLICATION, instanceName: this.instanceName, payload: data };
    }

    protected tryDeleteFeedPublication(publicationId: string): IFeedTryDeletePublication {
        return { type: FeedActionKeys.FEED_TRY_DELETE_PUBLICATION, instanceName: this.instanceName, payload: { publicationId } };
    }


    public feedReset(): IFeedResetAction {
        return { type: FeedActionKeys.FEED_RESET, instanceName: this.instanceName };
    }

    public addFeedData(data: FeedContentDto): IFeedAddAction {
        return { type: FeedActionKeys.FEED_ADD_DATA, instanceName: this.instanceName, payload: data };
    }

    public updateFeedData(data: FeedContentUpdate | FeedContentUpdate[]): IFeedUpdateAction {
        return { type: FeedActionKeys.FEED_UPDATE_DATA, instanceName: this.instanceName, payload: data };
    }

    public removeFeedData(uid: string, publicationId: string): IFeedDeleteAction {
        return { type: FeedActionKeys.FEED_DELETE_DATA, instanceName: this.instanceName, payload: { uid, publicationId } };
    }

    public executingFeedAction(uid: string, publicationId: string, running: boolean): IFeedExecutingAction {
        return { type: FeedActionKeys.FEED_EXECUTING_ACTION, instanceName: this.instanceName, payload: { uid, publicationId, running } };
    }


    public replaceFeedData(data: FeedContentReplace): IFeedReplaceAction {
        return { type: FeedActionKeys.FEED_REPLACE_DATA, instanceName: this.instanceName, payload: data };
    }

    public getPublicationFullContent(uid: string, publicationId: string): ThunkAction<Promise<void>, ApplicationState, void, Action> {
        return (dispatch) => {
            return new Promise<void>((resolve, reject) => {
                api.getPublicationFullContent(publicationId)
                    .then((fullContent) => {

                        if (fullContent)
                            dispatch(
                                this.updateFeedData({
                                    uid,
                                    items: [{ publicationId, body: { fullContent } }]
                                }));

                        resolve();
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        };
    }

    public ignoreActionRequest(uid: string, publicationId: string, actionId: string): ThunkAction<Promise<boolean>, ApplicationState, void, Action> {
        return (dispatch) => {
            dispatch(this.executingFeedAction(uid, publicationId, true));

            return new Promise<boolean>((resolve, reject) => {
                api.ignoreActionRequest(actionId)
                    .then((result) => {
                        if (result.saved) {
                            dispatch(this.removeFeedData(uid, publicationId));
                        }
                        dispatch(this.executingFeedAction(uid, publicationId, false));
                        dispatch(updateAppCallouts(result.callouts));
                        resolve(result.saved);
                    })
                    .catch((error) => {
                        dispatch(this.executingFeedAction(uid, publicationId, false));
                        reject(error);
                    });
            });
        };
    }

    public approvalActionRequest(uid: string, publicationId: string, actionId: string, approved: boolean, notes: string): ThunkAction<Promise<boolean>, ApplicationState, void, Action> {
        return (dispatch) => {
            dispatch(this.executingFeedAction(uid, publicationId, true));

            return new Promise<boolean>((resolve, reject) => {
                api.approvalActionRequest(actionId, approved, notes)
                    .then((result) => {
                        if (result.saved) {
                            dispatch(this.removeFeedData(uid, publicationId));
                        }
                        else {
                            dispatch(this.executingFeedAction(uid, publicationId, false));
                        }

                        if (result.callouts != null)
                            dispatch(updateAppCallouts(result.callouts));

                        resolve(result.saved);
                    })
                    .catch((error) => {
                        dispatch(this.executingFeedAction(uid, publicationId, false));
                        reject(error);
                    });
            });
        };
    }

    public addPublication(publicationId: string): ThunkAction<Promise<void>, ApplicationState, void, Action> {
        return (dispatch) => {
            return new Promise<void>((resolve, reject) => {
                api.getFeedItemPublication(publicationId, false)
                    .then((newItemDto) => {
                        dispatch(this.addFeedData({ uid: newItemDto.publicationId, items: [newItemDto] }));
                        resolve();
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        };
    }

    public changePublicationVisibility(uid: string, publicationId: string, visibility: PublicationVisility): ThunkAction<Promise<void>, ApplicationState, void, Action> {
        return (dispatch) => {
            return new Promise<void>((resolve, reject) => {
                api.postChangePublicationVisibility(publicationId, visibility)
                    .then((result) => {

                        if (result) {
                            if (visibility == PublicationVisility.None)
                                dispatch(this.removeFeedData(uid, publicationId));
                            else
                                dispatch(
                                    this.updateFeedData({
                                        uid,
                                        items: [{ publicationId, interaction: { visibility: visibility } }]
                                    }));
                        }

                        resolve();
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        };
    }

    public reloadPublication(uid: string, publicationId: string): ThunkAction<Promise<void>, ApplicationState, void, Action> {
        return (dispatch) => {

            dispatch(this.executingFeedAction(uid, publicationId, true));

            return new Promise<void>((resolve, reject) => {
                api.getFeedItemPublication(publicationId, false)
                    .then((item) => {
                        if (item) {
                            dispatch(this.replaceFeedData({ uid, items: [item] }));
                        }
                        dispatch(this.executingFeedAction(uid, publicationId, false));
                        resolve();
                    })
                    .catch(() => {
                        dispatch(this.executingFeedAction(uid, publicationId, false));
                        reject();
                    });
            });
        };
    }

    public publishPublication(uid: string, publicationId: string, publicationType: PublicationType): ThunkAction<Promise<void>, ApplicationState, void, Action> {
        return (dispatch) => {

            return new Promise<void>((resolve, reject) => {
                dispatch(this.executingFeedAction(uid, publicationId, true));

                api.publishDraftPublication(publicationId, publicationType)
                    .then((result) => {
                        if (result) {
                            api.getFeedItemPublication(publicationId, true)
                                .then((item) => {
                                    if (item) {
                                        dispatch(this.replaceFeedData({ uid, items: [item] }));
                                    }
                                    dispatch(this.executingFeedAction(uid, publicationId, false));
                                    resolve();
                                })
                                .catch(() => {
                                    dispatch(this.executingFeedAction(uid, publicationId, false));
                                    resolve();
                                });
                        }
                        resolve();
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        };
    }

    public removePublication(uid: string, publicationId: string, publicationType: PublicationType): ThunkAction<Promise<boolean>, ApplicationState, void, Action> {
        return (dispatch) => {

            return new Promise<boolean>((resolve, reject) => {
                dispatch(this.executingFeedAction(uid, publicationId, true));

                api.deletePublication(publicationId, publicationType)
                    .then((result) => {
                        if (result) {
                            dispatch(this.deleteSuccess(uid, publicationId));
                        }
                        dispatch(this.executingFeedAction(uid, publicationId, false));
                        resolve(result);
                    })
                    .catch((error) => {
                        dispatch(this.executingFeedAction(uid, publicationId, false));
                        reject(error);
                    });
            });
        };
    }

    public changePublicationExecutingProperty(uid: string, publicationId: string, value: boolean): ThunkAction<void, ApplicationState, void, Action> {
        return (dispatch) => {
            dispatch(this.executingFeedAction(uid, publicationId, value));
        };
    }

    public executeFeedMessage(messageType: FeedMessageTypes, dispatch: any) {

        if (!messageType)
            return;

        switch (messageType.type) {
            case FeedMessageKeys.TRY_ADD_PUBLICATION:
                dispatch(this.addPublication(messageType.item.publicationId));
                break;
            case FeedMessageKeys.TRY_UPDATE_PUBLICATION:
                dispatch(this.tryUpdateFeedData(messageType.item));
                break;
            case FeedMessageKeys.TRY_DELETE_PUBLICATION:
                dispatch(this.tryDeleteFeedPublication(messageType.publicationId));
                break;
            case FeedMessageKeys.TRY_FEED_RESET:
                dispatch(this.feedReset());
                break;
        }
    }
}


//////////////////////////////////////////////////////

export class MainFeedAction extends FeedActions {

    constructor() {
        super(FeedInstances.Main);
    }


    public executeFeedMessage(messageType: FeedMessageTypes, dispatch?: any) {

        if (!messageType)
            return;

        super.executeFeedMessage(messageType, dispatch);
    }

    public getFeedMain(startIndex: number, endIndex: number): ThunkAction<Promise<void>, ApplicationState, void, Action> {
        var reqid = ++lastRequest;

        return (dispatch) => {

            return new Promise<void>((resolve) => {
                dispatch(this.fetchStarted());

                api.getFeedMain({
                    sessionId: Session.SessionId,
                    range: { offset: startIndex, take: endIndex - startIndex + 1 }
                })
                    .then((result) => {
                        if (reqid == lastRequest) {
                            dispatch(this.fetchSuccess(computeFeedResult(startIndex, endIndex, result)));
                            resolve();
                        }
                    })
                    .catch((error) => {
                        dispatch(this.fetchFailure(error));
                        resolve();
                    });
            });
        };
    }
}

export class PublicationsFeedAction extends FeedActions {

    constructor() {
        super(FeedInstances.Main);
    }

    public executeFeedMessage(messageType: FeedMessageTypes, dispatch: any) {

        if (!messageType)
            return;

        super.executeFeedMessage(messageType, dispatch);
    }

    public getFeed(filter: FeedPublicationFilter, orderBy: FeedBaseOrderBy, startIndex: number, endIndex: number): ThunkAction<Promise<void>, ApplicationState, void, Action> {
        var reqid = ++lastRequest;

        return (dispatch) => {

            return new Promise<void>((resolve) => {
                dispatch(this.fetchStarted());

                api.getFeedPublications({
                    filter,
                    orderBy,
                    range: { offset: startIndex, take: endIndex - startIndex + 1 }
                })
                    .then((result) => {
                        if (reqid == lastRequest) {
                            dispatch(this.fetchSuccess(computeFeedResult(startIndex, endIndex, result)));

                            if (startIndex == 0 && filter.category == FeedType.MyPublication) {
                                dispatch(updateAppCallouts([{ feed: filter.category, topicId: null, callout: false }]));
                            }

                            resolve();
                        }
                    })
                    .catch((error) => {
                        dispatch(this.fetchFailure(error));
                        resolve();
                    });
            });
        };
    }

}

export class FollowingFeedAction extends FeedActions {

    constructor() {
        super(FeedInstances.Main);
    }

    public getFeed(filter: FeedFollowingFilter, orderBy: FeedBaseOrderBy, startIndex: number, endIndex: number): ThunkAction<Promise<void>, ApplicationState, void, Action> {
        var reqid = ++lastRequest;

        return (dispatch) => {

            return new Promise<void>((resolve) => {

                dispatch(this.fetchStarted());

                api.getFeedFollowing({
                    sessionId: Session.SessionId,
                    filter,
                    orderBy,
                    range: { offset: startIndex, take: endIndex - startIndex + 1 }
                })
                    .then((result) => {
                        if (reqid == lastRequest) {
                            dispatch(this.fetchSuccess(computeFeedResult(startIndex, endIndex, result)));
                            resolve();
                        }
                    })
                    .catch((error) => {
                        dispatch(this.fetchFailure(error));
                        resolve();
                    });
            });
        };
    }

    public executeFeedMessage(messageType: FeedMessageTypes, dispatch: any) {

        if (!messageType)
            return;

        super.executeFeedMessage(messageType, dispatch);
    }
}

export class QuestionsFeedAction extends FeedActions {

    constructor() {
        super(FeedInstances.Main);
    }

    public getFeed(filter: FeedQuestionsFilter, startIndex: number, endIndex: number): ThunkAction<Promise<void>, ApplicationState, void, Action> {
        var reqid = ++lastRequest;

        return (dispatch) => {

            return new Promise<void>((resolve) => {
                dispatch(this.fetchStarted());

                api.getFeedQuestions({
                    sessionId: Session.SessionId,
                    filter,
                    range: { offset: startIndex, take: endIndex - startIndex + 1 }
                })
                    .then((result) => {
                        if (reqid == lastRequest) {

                            dispatch(this.fetchSuccess(computeFeedResult(startIndex, endIndex, result)));

                            if (startIndex == 0) {
                                dispatch(updateAppCallouts([{ feed: filter.category, topicId: null, callout: false }]));
                            }

                            resolve();
                        }
                    })
                    .catch((error) => {
                        dispatch(this.fetchFailure(error));
                        resolve();
                    });
            });
        };
    }


    public executeFeedMessage(messageType: FeedMessageTypes, dispatch: any) {

        if (!messageType)
            return;

        super.executeFeedMessage(messageType, dispatch);
    }
}

export class HighlightsFeedAction extends FeedActions {

    constructor() {
        super(FeedInstances.Main);
    }

    public getFeed(filter: FeedHighlightsFilter, startIndex: number, endIndex: number): ThunkAction<Promise<void>, ApplicationState, void, Action> {
        var reqid = ++lastRequest;

        return (dispatch) => {

            return new Promise<void>((resolve) => {
                dispatch(this.fetchStarted());

                api.getFeedHighlights({
                    sessionId: Session.SessionId,
                    filter,
                    range: { offset: startIndex, take: endIndex - startIndex + 1 }
                })
                    .then((result) => {
                        if (reqid == lastRequest) {
                            dispatch(this.fetchSuccess(computeFeedResult(startIndex, endIndex, result)));
                            resolve();
                        }
                    })
                    .catch((error) => {
                        dispatch(this.fetchFailure(error));
                        resolve();
                    });
            });
        };
    }


    public executeFeedMessage(messageType: FeedMessageTypes, dispatch: any) {

        if (!messageType)
            return;

        super.executeFeedMessage(messageType, dispatch);
    }
}

export class ActionsFeedAction extends FeedActions {

    constructor() {
        super(FeedInstances.Main);
    }

    public getFeed(filter: FeedActionsFilter, startIndex: number, endIndex: number): ThunkAction<Promise<void>, ApplicationState, void, Action> {
        var reqid = ++lastRequest;

        return (dispatch) => {

            return new Promise<void>((resolve) => {
                dispatch(this.fetchStarted());

                api.getFeedActions({
                    sessionId: Session.SessionId,
                    filter,
                    range: { offset: startIndex, take: endIndex - startIndex + 1 }
                })
                    .then((result) => {
                        if (reqid == lastRequest) {
                            dispatch(this.fetchSuccess(computeFeedResult(startIndex, endIndex, result)));
                            resolve();
                        }
                    })
                    .catch((error) => {
                        dispatch(this.fetchFailure(error));
                        resolve();
                    });
            });
        };
    }


    public executeFeedMessage(messageType: FeedMessageTypes, dispatch: any) {

        if (!messageType)
            return;

        super.executeFeedMessage(messageType, dispatch);
    }
}

export class HistoryFeedAction extends FeedActions {

    constructor() {
        super(FeedInstances.Main);
    }

    public getFeed(filter: FeedHistoryFilter, startIndex: number, endIndex: number): ThunkAction<Promise<void>, ApplicationState, void, Action> {
        var reqid = ++lastRequest;

        return (dispatch) => {

            return new Promise<void>((resolve) => {
                dispatch(this.fetchStarted());

                api.getFeedHistory(
                    {
                        filter,
                        range: { offset: startIndex, take: endIndex - startIndex + 1 }
                    })
                    .then((result) => {
                        if (reqid == lastRequest) {
                            dispatch(this.fetchSuccess(computeFeedResult(startIndex, endIndex, result)));
                            resolve();
                        }
                    })
                    .catch((error) => {
                        dispatch(this.fetchFailure(error));
                        resolve();
                    });
            });
        };
    }


    public executeFeedMessage(messageType: FeedMessageTypes, dispatch: any) {
        return;
    }
}

export class AnswersFeedAction extends FeedActions {

    constructor(private questionId: string) {
        super(FeedInstances.Answers);
    }

    public removePublication(uid: string, publicationId: string, publicationType: PublicationType): ThunkAction<Promise<boolean>, ApplicationState, void, Action> {
        return (dispatch) => {

            return new Promise<boolean>((resolve, reject) => {
                api.deleteAnswer(publicationId)
                    .then((result) => {
                        if (result) {
                            dispatch(this.deleteSuccess(uid, publicationId));
                        }
                        resolve(result);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        };
    }

    public getFeed(filter: FeedAnswersFilter, orderBy: FeedAnswersOrderBy, startIndex: number, endIndex: number): ThunkAction<Promise<void>, ApplicationState, void, Action> {
        var reqid = ++lastRequest;

        return (dispatch) => {

            return new Promise<void>((resolve) => {
                dispatch(this.fetchStarted());

                filter.questionId = this.questionId;

                api.getFeedAnswers(
                    {
                        filter,
                        orderBy,
                        range: { offset: startIndex, take: endIndex - startIndex + 1 }
                    })
                    .then((result) => {
                        if (reqid == lastRequest) {
                            dispatch(this.fetchSuccess(computeFeedResult(startIndex, endIndex, result)));
                            resolve();
                        }
                    })
                    .catch((error) => {
                        dispatch(this.fetchFailure(error));
                        resolve();
                    });
            });
        };
    }


    public executeFeedMessage(messageType: FeedMessageTypes, dispatch: any) {

        if (!messageType || messageType.type == FeedMessageKeys.TRY_ADD_PUBLICATION)
            return;

        super.executeFeedMessage(messageType, dispatch);
    }
}

export class ListFeedAction extends FeedActions {

    constructor(private listId: string) {
        super(FeedInstances.Private);
    }

    public processSuggestionApproval(uid: string, publicationId: string, actionId: string, approved: boolean, notes: string): ThunkAction<Promise<boolean>, ApplicationState, void, Action> {
        return (dispatch) => {
            return new Promise<boolean>((resolve, reject) => {
                api.postSuggestionApproval(actionId, approved, notes)
                    .then((result) => {
                        if (result) {

                            if (approved) {
                                api.getFeedListItemPublication(this.listId, publicationId, false)
                                    .then((item) => {
                                        if (item) {
                                            dispatch(this.replaceFeedData({ uid, items: [item] }));
                                        }
                                        resolve(result);
                                    })
                                    .catch(() => {
                                        resolve(false);
                                    });
                            }
                            else {
                                if (result) dispatch(this.removeFeedData(uid, publicationId));
                                resolve(result);
                            }

                        }
                        else
                            resolve(result);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        };
    }

    public reloadPublication(uid: string, publicationId: string): ThunkAction<Promise<void>, ApplicationState, void, Action> {
        return (dispatch) => {
            return new Promise<void>((resolve, reject) => {
                dispatch(this.executingFeedAction(uid, publicationId, true));

                api.getFeedListItemPublication(this.listId, publicationId, false)
                    .then((item) => {
                        if (item) {
                            dispatch(this.replaceFeedData({ uid, items: [item] }));
                        }
                        dispatch(this.executingFeedAction(uid, publicationId, false));
                        resolve();
                    })
                    .catch((error) => {
                        dispatch(this.executingFeedAction(uid, publicationId, false));
                        reject(error);
                    });
            });
        };
    }

    public addItemPublication(publicationId: string): ThunkAction<Promise<void>, ApplicationState, void, Action> {
        return (dispatch) => {
            return new Promise<void>((resolve, reject) => {
                api.getFeedListItemPublication(this.listId, publicationId, false)
                    .then((newItemDto) => {
                        dispatch(this.addFeedData({ uid: newItemDto.publicationId, items: [newItemDto] }));
                        resolve();
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        };
    }

    public removeItemPublication(uid: string, publicationId: string): ThunkAction<Promise<boolean>, ApplicationState, void, Action> {
        return (dispatch) => {
            return new Promise<boolean>((resolve, reject) => {
                api.listRemovePublication(this.listId, publicationId)
                    .then((result) => {
                        if (result) dispatch(this.deleteSuccess(uid, publicationId));
                        resolve(true);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        };
    }

    public getFeed(filter: FeedListFilter, orderBy: FeedListOrderBy, startIndex: number, endIndex: number): ThunkAction<Promise<void>, ApplicationState, void, Action> {
        var reqid = ++lastRequest;

        return (dispatch) => {

            return new Promise<void>((resolve) => {

                dispatch(this.fetchStarted());

                filter.listId = this.listId;

                api.getFeedList(
                    {
                        sessionId: Session.SessionId,
                        filter,
                        orderBy,
                        range: { offset: startIndex, take: endIndex - startIndex + 1 }
                    })
                    .then((result) => {
                        if (reqid == lastRequest)
                            dispatch(this.fetchSuccess(computeFeedResult(startIndex, endIndex, result)));
                        resolve();
                    })
                    .catch((error) => {
                        dispatch(this.fetchFailure(error));
                        resolve();
                    });
            });
        };
    }

    public executeFeedMessage(messageType: FeedMessageTypes, dispatch: any) {

        if (!messageType || messageType.type == FeedMessageKeys.TRY_ADD_PUBLICATION)
            return;

        super.executeFeedMessage(messageType, dispatch);
    }

}


export class CourseFeedAction extends FeedActions {

    constructor() {
        super(FeedInstances.Main);
    }

    public getFeed(filter: FeedCourseFilter, orderBy: FeedCourseOrderBy, startIndex: number, endIndex: number): ThunkAction<Promise<void>, ApplicationState, void, Action> {
        var reqid = ++lastRequest;

        return (dispatch) => {

            return new Promise<void>((resolve) => {
                dispatch(this.fetchStarted());

                api.getFeedCourse(
                    {
                        filter,
                        orderBy,
                        range: { offset: startIndex, take: endIndex - startIndex + 1 }
                    })
                    .then((result) => {
                        if (reqid == lastRequest) {
                            dispatch(this.fetchSuccess(computeFeedResult(startIndex, endIndex, result)));
                            resolve();
                        }
                    })
                    .catch((error) => {
                        dispatch(this.fetchFailure(error));
                        resolve();
                    });
            });
        };
    }

    public customActions(uid: string, publicationId: string, publicationType: PublicationType, action: CourseActions): ThunkAction<Promise<boolean>, ApplicationState, void, Action> {
        return (dispatch) => {
            dispatch(this.executingFeedAction(uid, publicationId, true));

            return new Promise<boolean>((resolve, reject) => {

                if (action.feed && action.feed.actionName == "enroll") {
                    api.courseEnroll(publicationId, Session.user.id)
                        .then((result) => {
                            if (result) {
                                dispatch(this.removeFeedData(uid, publicationId));
                            }
                            else {
                                dispatch(this.executingFeedAction(uid, publicationId, false));
                            }
                            resolve(result != null);
                        })
                        .catch((error) => {
                            dispatch(this.executingFeedAction(uid, publicationId, false));
                            reject(error);
                        });
                }
                else if (action.feed && action.feed.actionName == "cancelEnroll") {
                    api.courseCancelEnroll(publicationId, Session.user.id)
                        .then((result) => {
                            if (result) {
                                dispatch(this.removeFeedData(uid, publicationId));
                            }
                            else {
                                dispatch(this.executingFeedAction(uid, publicationId, false));
                            }
                            resolve(result != null);
                        })
                        .catch((error) => {
                            dispatch(this.executingFeedAction(uid, publicationId, false));
                            reject(error);
                        });
                }
                else {
                    dispatch(this.executingFeedAction(uid, publicationId, false));
                    resolve(false);
                }
            });
        };
    }

    public executeFeedMessage(messageType: FeedMessageTypes, dispatch: any) {
        return;
    }
}

export class CourseEnrollFeedAction extends FeedActions {

    constructor(private courseId: string) {
        super(FeedInstances.Private);
    }

    public customAction(uid: string, publicationId: string, promiseAction: Promise<CourseItemCompleteResult>): ThunkAction<Promise<CourseItemCompleteResult>, ApplicationState, void, Action> {
        return (dispatch) => {
            dispatch(this.executingFeedAction(uid, publicationId, true));

            return new Promise<CourseItemCompleteResult>((resolve, reject) => {

                promiseAction
                    .then((result) => {
                        dispatch(this.executingFeedAction(uid, publicationId, false));
                        resolve(result);
                    })
                    .catch((error) => {
                        dispatch(this.executingFeedAction(uid, publicationId, false));
                        reject(error);
                    });
            });
        };
    }

    public reloadPublication(uid: string, itemId: string): ThunkAction<Promise<void>, ApplicationState, void, Action> {
        return (dispatch) => {
            return new Promise<void>((resolve, reject) => {
                dispatch(this.feedReset());
                resolve();
            });
        };
    }

    public getFeed(filter: FeedCourseEnrollFilter, startIndex: number, endIndex: number): ThunkAction<Promise<void>, ApplicationState, void, Action> {
        var reqid = ++lastRequest;

        return (dispatch) => {

            return new Promise<void>((resolve) => {

                dispatch(this.fetchStarted());

                filter.courseId = this.courseId;

                api.getFeedEnrolledCourseItems(
                    {
                        filter,
                        range: { offset: startIndex, take: endIndex - startIndex + 1 }
                    })
                    .then((result) => {
                        if (reqid == lastRequest) {
                            dispatch(this.fetchSuccess(computeFeedResult(startIndex, endIndex, result)));
                        }
                        resolve();
                    })
                    .catch((error) => {
                        dispatch(this.fetchFailure(error));
                        resolve();
                    });
            });
        };
    }
}

export class CourseListFeedAction extends FeedActions {

    constructor(private courseId: string) {
        super(FeedInstances.Private);
    }

    public reloadPublication(uid: string, itemId: string): ThunkAction<Promise<void>, ApplicationState, void, Action> {
        return (dispatch) => {
            return new Promise<void>((resolve, reject) => {
                dispatch(this.executingFeedAction(uid, itemId, true));

                api.getFeedItemOfCourseItem(this.courseId, itemId)
                    .then((item) => {
                        if (item) {
                            dispatch(this.replaceFeedData({ uid, items: [item] }));
                        }
                        dispatch(this.executingFeedAction(uid, itemId, false));
                        resolve();
                    })
                    .catch((error) => {
                        dispatch(this.executingFeedAction(uid, itemId, false));
                        reject(error);
                    });
            });
        };
    }

    public removeItemPublication(uid: string, publicationId: string): ThunkAction<Promise<CourseItemOperationResult>, ApplicationState, void, Action> {
        return (dispatch) => {
            return new Promise<CourseItemOperationResult>((resolve, reject) => {
                dispatch(this.executingFeedAction(uid, publicationId, true));

                api.courseRemovePublication(this.courseId, publicationId)
                    .then((result) => {
                        if (result.saved && result.hardDelete) dispatch(this.deleteSuccess(uid, publicationId));
                        dispatch(this.executingFeedAction(uid, publicationId, false));
                        resolve(result);
                    })
                    .catch((error) => {
                        dispatch(this.executingFeedAction(uid, publicationId, false));
                        reject(error);
                    });
            });
        };
    }

    public getFeed(filter: FeedCourseListFilter, startIndex: number, endIndex: number): ThunkAction<Promise<void>, ApplicationState, void, Action> {
        var reqid = ++lastRequest;

        return (dispatch) => {

            return new Promise<void>((resolve) => {

                dispatch(this.fetchStarted());

                filter.courseId = this.courseId;

                api.getFeedCourseItems(
                    {
                        filter,
                        range: { offset: startIndex, take: endIndex - startIndex + 1 }
                    })
                    .then((result) => {
                        if (reqid == lastRequest)
                            dispatch(this.fetchSuccess(computeFeedResult(startIndex, endIndex, result)));
                        resolve();
                    })
                    .catch((error) => {
                        dispatch(this.fetchFailure(error));
                        resolve();
                    });
            });
        };
    }

    public executeFeedMessage(messageType: FeedMessageTypes, dispatch: any) {

        if (!messageType || messageType.type == FeedMessageKeys.TRY_ADD_PUBLICATION)
            return;

        super.executeFeedMessage(messageType, dispatch);
    }
}


export class TopicFeedAction extends FeedActions {

    constructor(private topicId: string) {
        super(FeedInstances.Main);
    }

    public reloadPublication(uid: string, publicationId: string): ThunkAction<Promise<void>, ApplicationState, void, Action> {
        return (dispatch) => {
            return new Promise<void>((resolve) => {
                api.getTopicPublication(this.topicId, publicationId)
                    .then((item) => {
                        if (item) {
                            dispatch(this.replaceFeedData({ uid, items: [item] }));
                        }
                        resolve();
                    })
                    .catch(() => {
                        resolve();
                    });
            });
        };
    }

    public executeFeedMessage(messageType: FeedMessageTypes, dispatch: any) {

        if (!messageType || messageType.type == FeedMessageKeys.TRY_ADD_PUBLICATION)
            return;

        super.executeFeedMessage(messageType, dispatch);
    }

    public getFeed(filter: FeedTopicFilter, orderBy: FeedTopicOrderBy, startIndex: number, endIndex: number): ThunkAction<Promise<void>, ApplicationState, void, Action> {
        var reqid = ++lastRequest;

        return (dispatch) => {

            return new Promise<void>((resolve) => {

                dispatch(this.fetchStarted());

                filter.topicId = this.topicId;

                api.getTopicFeedPublications(
                    {
                        filter,
                        orderBy,
                        range: { offset: startIndex, take: endIndex - startIndex + 1 }
                    })
                    .then((result) => {
                        if (reqid == lastRequest) {

                            dispatch(this.fetchSuccess(computeFeedResult(startIndex, endIndex, result)));

                            if (startIndex == 0) {
                                dispatch(updateAppCallouts(
                                    [
                                        { feed: FeedType.Topic, topicId: filter.topicId, callout: false }
                                    ]
                                ));
                            }

                        }
                        resolve();
                    })
                    .catch((error) => {
                        dispatch(this.fetchFailure(error));
                        resolve();
                    });
            });
        };
    }

}

export class UserProfileFeedAction extends FeedActions {

    constructor(private userId: string) {
        super(FeedInstances.Private);
    }

    public getFeed(filter: TimelineUserProfileFilter, orderBy: FeedBaseOrderBy, startIndex: number, endIndex: number): ThunkAction<Promise<void>, ApplicationState, void, Action> {
        var reqid = ++lastRequest;

        return (dispatch) => {

            return new Promise<void>((resolve) => {

                dispatch(this.fetchStarted());

                filter.userId = this.userId;

                api.getFeedUserProfile(
                    {
                        sessionId: Session.SessionId,
                        filter,
                        orderBy,
                        range: { offset: startIndex, take: endIndex - startIndex + 1 }
                    })
                    .then((result) => {
                        if (reqid == lastRequest)
                            dispatch(this.fetchSuccess(computeFeedResult(startIndex, endIndex, result)));
                        resolve();
                    })
                    .catch((error) => {
                        dispatch(this.fetchFailure(error));
                        resolve();
                    });
            });
        };
    }

}

